import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  Textarea,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useToast,
  Text,
  Badge,
  Box,
  useColorModeValue,
  Divider,
  ColorPicker,
  Flex,
  Select,
  Center,
  Spinner
} from '@chakra-ui/react';
import { FaTrash, FaPlus, FaSave, FaFolder } from 'react-icons/fa';
import { HiColorSwatch } from 'react-icons/hi';
import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

const CategoryManager = ({ isOpen, onClose, clienteId, onCategoryChange }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    color: '#3E5F56',
    icon: 'FaFolder'
  });
  const [isCreating, setIsCreating] = useState(false);

  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('white', 'gray.800');

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/clientes/${clienteId}/documents/categories`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: 'Erro ao carregar categorias',
        description: error.response?.data?.message || 'Erro ao carregar categorias',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen, clienteId]);

  const handleCreateCategory = async () => {
    if (!newCategory.name) {
      toast({
        title: 'Nome obrigatório',
        description: 'Por favor, insira um nome para a categoria',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsCreating(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/clientes/${clienteId}/documents/categories`,
        newCategory,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: 'Categoria criada',
        description: 'Categoria criada com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setNewCategory({ name: '', description: '', color: '#3E5F56', icon: 'FaFolder' });
      fetchCategories();
      if (onCategoryChange) onCategoryChange();
    } catch (error) {
      console.error('Error creating category:', error);
      toast({
        title: 'Erro ao criar categoria',
        description: error.response?.data?.message || 'Erro ao criar categoria',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/api/clientes/${clienteId}/documents/categories/${categoryId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: 'Categoria excluída',
        description: 'Categoria excluída com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchCategories();
      if (onCategoryChange) onCategoryChange();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: 'Erro ao excluir categoria',
        description: error.response?.data?.message || 'Erro ao excluir categoria',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const predefinedColors = [
    '#3E5F56', '#E53E3E', '#38A169', '#3182CE', '#805AD5',
    '#D69E2E', '#ED8936', '#38B2AC', '#E53E3E', '#319795'
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Gerenciar Categorias</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Create new category form */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" mb={3}>
                Nova Categoria
              </Text>
              <VStack spacing={3} p={4} borderWidth="1px" borderColor={borderColor} borderRadius="md">
                <FormControl isRequired>
                  <FormLabel>Nome</FormLabel>
                  <Input
                    placeholder="Nome da categoria"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Descrição</FormLabel>
                  <Textarea
                    placeholder="Descrição da categoria (opcional)"
                    value={newCategory.description}
                    onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                    rows={2}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Cor</FormLabel>
                  <HStack spacing={2}>
                    <Input
                      type="color"
                      value={newCategory.color}
                      onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                      w="50px"
                      h="40px"
                      p={1}
                      borderRadius="md"
                    />
                    <HStack spacing={1} wrap="wrap">
                      {predefinedColors.map((color) => (
                        <Box
                          key={color}
                          w="30px"
                          h="30px"
                          bg={color}
                          borderRadius="md"
                          cursor="pointer"
                          border="2px solid"
                          borderColor={newCategory.color === color ? 'blue.500' : 'transparent'}
                          onClick={() => setNewCategory({ ...newCategory, color })}
                        />
                      ))}
                    </HStack>
                  </HStack>
                </FormControl>

                <Button
                  colorScheme="green"
                  leftIcon={<FaPlus />}
                  onClick={handleCreateCategory}
                  isLoading={isCreating}
                  loadingText="Criando..."
                  alignSelf="flex-end"
                >
                  Criar Categoria
                </Button>
              </VStack>
            </Box>

            <Divider />

            {/* Existing categories */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" mb={3}>
                Categorias Existentes
              </Text>
              {loading ? (
                <Center p={5}>
                  <Spinner size="lg" color="green.500" />
                </Center>
              ) : categories.length === 0 ? (
                <Text color="gray.500" textAlign="center" p={5}>
                  Nenhuma categoria criada ainda
                </Text>
              ) : (
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Cor</Th>
                      <Th>Nome</Th>
                      <Th>Descrição</Th>
                      <Th>Documentos</Th>
                      <Th>Criado por</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {categories.map((category) => (
                      <Tr key={category._id}>
                        <Td>
                          <Box
                            w="20px"
                            h="20px"
                            bg={category.color}
                            borderRadius="md"
                          />
                        </Td>
                        <Td fontWeight="medium">{category.name}</Td>
                        <Td>{category.description || '-'}</Td>
                        <Td>
                          <Badge colorScheme="gray">
                            {category.documentCount || 0}
                          </Badge>
                        </Td>
                        <Td>{category.createdBy?.nomeCompleto || 'N/A'}</Td>
                        <Td>
                          <IconButton
                            size="sm"
                            icon={<FaTrash />}
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDeleteCategory(category._id)}
                            isDisabled={category.documentCount > 0}
                            title={
                              category.documentCount > 0
                                ? 'Não é possível excluir categoria com documentos'
                                : 'Excluir categoria'
                            }
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CategoryManager;
